import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Dialog } from "@mui/material";
import { Copy, ExternalLink, X } from "lucide-react";
import { copyTextToClipboard } from "../../utils/clipboard";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import {
  FLAG_LABEL,
  REVIEW_REASON_LABEL,
  handleHumanize,
} from "../../utils/constants/AdIntelligence";
import { Button } from "../ui/button";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useTaxonomyOptions from "../../hooks/useTaxonomyOptions";
import CreativeAnalysisAPIs from "../../apiRoutes/CreativeAnalysis";
import StatusChip from "../common/StatusChip";
import AdVideo from "./AdVideo";
import TagEditor from "./TagEditor";

const Section = ({ title, action = null, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white">
    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {title}
      </h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

/** One tag beside its video, storyboard and transcript, edited in place and approved or saved. */
const TagReviewDialog = ({
  tag,
  queue,
  canApprove,
  hasNext,
  onClose,
  onReviewed,
}) => {
  const navigate = useNavigate();
  const openedAt = useRef(Date.now());
  const axiosPrivate = useAxiosPrivate();
  const { data: options, isError: optionsFailed } = useTaxonomyOptions({
    category: tag.category,
  });
  const reviewMutation = useMutation({
    mutationFn: (payload) =>
      CreativeAnalysisAPIs.handleReviewTag({
        axiosPrivate,
        id: tag.id,
        payload: {
          ...payload,
          queue,
          seconds_spent: Math.round((Date.now() - openedAt.current) / 1000),
        },
      }),
    onSuccess: (_data, payload) => {
      toast.success(
        payload.verdict === "approve" ? "Tag approved." : "Changes saved."
      );
      onReviewed();
    },
  });
  const needsReview = tag.status === "needs_review";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      disableEnforceFocus
      sx={{ zIndex: 999 }}
      PaperProps={{
        sx: {
          width: "min(1280px, 96vw)",
          height: "92vh",
          m: 1,
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      <div className="flex h-full flex-col bg-gray-50">
        <div className="flex items-start gap-3 border-b border-gray-200 bg-white px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-gray-900">
              {tag.hook_name || tag.ad_name || `Delivery #${tag.ad_deliver_id}`}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {handleHumanize({ code: tag.category })} · Delivery #
              {tag.ad_deliver_id} · Hook {tag.hook_number} · Revision{" "}
              {tag.revision}
              {tag.model && ` · ${tag.model}`}
              {tag.tagged_at &&
                ` · Tagged ${formatDisplayDateTime(tag.tagged_at)}`}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <StatusChip status={`tag_${tag.status}`} subtle />
              {tag.confidence && (
                <StatusChip status={`confidence_${tag.confidence}`} subtle />
              )}
              <StatusChip status={`tag_review_${tag.review_status}`} subtle />
              {tag.flags.map((flag) => (
                <StatusChip
                  key={flag}
                  status={flag}
                  label={FLAG_LABEL[flag] || handleHumanize({ code: flag })}
                  subtle
                />
              ))}
            </div>
          </div>
          {tag.ad_request_id && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/dashboard/ad-request/${tag.ad_request_id}`)
              }
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Ad request
            </Button>
          )}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid flex-1 gap-4 overflow-y-auto p-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="space-y-4">
            <Section title="Ad video">
              <AdVideo tag={tag} />
            </Section>
            <Section title="Storyboard">
              {tag.storyboard_url ? (
                <a href={tag.storyboard_url} target="_blank" rel="noreferrer">
                  <img
                    src={tag.storyboard_url}
                    alt="Storyboard"
                    loading="lazy"
                    className="w-full rounded-lg border border-gray-100"
                  />
                </a>
              ) : (
                <p className="text-sm text-gray-500">No storyboard yet.</p>
              )}
            </Section>
            <Section
              title="Transcript"
              action={
                tag.transcript && (
                  <button
                    type="button"
                    aria-label="Copy transcript"
                    onClick={() =>
                      copyTextToClipboard(tag.transcript, "Transcript copied")
                    }
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )
              }
            >
              <p className="max-h-[360px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
                {tag.transcript || "No transcript."}
              </p>
            </Section>
          </div>

          <div className="space-y-4">
            {needsReview && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {REVIEW_REASON_LABEL[tag.review_reason] ||
                  handleHumanize({ code: tag.review_reason })}
                . The ad has no tag until it is fixed.
              </div>
            )}
            <Section title="Tag">
              <TagEditor
                tag={tag}
                options={options}
                optionsFailed={optionsFailed}
                canApprove={canApprove}
                hasNext={hasNext}
                isSaving={reviewMutation.isPending}
                onApprove={({ feedback, notes }) =>
                  reviewMutation.mutate({ verdict: "approve", feedback, notes })
                }
                onSave={({ fields, reasons, feedback, notes }) =>
                  reviewMutation.mutate({
                    verdict: "correct",
                    fields,
                    reasons,
                    feedback,
                    notes,
                  })
                }
              />
            </Section>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TagReviewDialog;
